import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowRight, CheckCircle, Phone, Headphones, MessageSquare, BarChart3, Users } from 'lucide-react'
import { SEO } from '@/lib/seo'
import { Button, Badge, AnimatedCard, CardHeader, CardTitle, CardDescription } from '@/components/ui'
import { CTASection } from '@/components/sections'
import { getServiceById } from '@/data/services'
import { companyInfo } from '@/data/company'

export function CCaaS() {
  const service = getServiceById('ccaas')!

  return (
    <>
      <SEO
        title="CCaaS - Contact Center as a Service"
        description="Deliver exceptional customer experiences with an intelligent, omnichannel contact center solution. Zoom Certified installer and support specialist."
      />

      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-50" />
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="glow" className="mb-6">CCaaS Solutions</Badge>
              
              <h1 className="text-display-md md:text-display-lg lg:text-display-xl font-heading font-bold text-white mb-6">
                {service.title}
              </h1>

              <p className="text-lg text-slate-300 mb-8">
                {service.longDescription}
              </p>

              {/* Zoom Certification Badges */}
              <div className="flex flex-wrap gap-4 mb-8">
                {service.certifications?.map((cert) => (
                  <div
                    key={cert}
                    className="flex items-center gap-3 glass-card px-4 py-3 rounded-xl"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <span className="text-blue-400 font-bold text-lg">Z</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-white">{cert}</div>
                      <div className="text-xs text-slate-400">Verified Partner</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" variant="glow" className="group">
                    Get CCaaS Quote
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.phoneRaw}`}>
                  <Button size="lg" variant="secondary">
                    <Phone className="w-5 h-5" />
                    {companyInfo.phone}
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="hidden lg:block"
            >
              <div className="glass-card p-8 rounded-3xl">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Headphones, label: 'Voice', desc: 'Intelligent call routing' },
                    { icon: MessageSquare, label: 'Omnichannel', desc: 'Chat, SMS, email, social' },
                    { icon: BarChart3, label: 'Analytics', desc: 'Real-time dashboards' },
                    { icon: Users, label: 'Workforce', desc: 'Agent management' },
                  ].map((item, index) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                      className="glass-card-hover p-6"
                    >
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-500 to-electric-500 flex items-center justify-center mb-4 shadow-glow">
                        <item.icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-semibold text-white mb-1">{item.label}</h3>
                      <p className="text-sm text-slate-400">{item.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Features</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Transform Customer Engagement
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Our CCaaS platform provides everything you need to deliver exceptional customer experiences.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {service.features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <AnimatedCard className="h-full">
                  <CardHeader>
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-500/20 to-electric-500/20 flex items-center justify-center mb-4">
                      <feature.icon className="w-7 h-7 text-accent-400" />
                    </div>
                    <CardTitle>{feature.title}</CardTitle>
                    <CardDescription className="text-base">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                </AnimatedCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-4">Benefits</Badge>
              <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-6">
                Why Choose Our CCaaS Solutions?
              </h2>
              <p className="text-lg text-slate-400 mb-8">
                Partner with Zoom Certified experts to transform your contact center into a competitive advantage.
              </p>

              <div className="space-y-4">
                {service.benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-8 h-8 rounded-full bg-success-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-success-500" />
                    </div>
                    <span className="text-slate-300">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-card p-8 rounded-3xl"
            >
              <h3 className="text-xl font-heading font-semibold text-white mb-6">
                Zoom Contact Center Excellence
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-400 font-bold text-lg">Z</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-2">Zoom Contact Center</h4>
                    <p className="text-sm text-slate-400">
                      We're certified to deploy Zoom Contact Center, bringing AI-powered customer engagement to your organization.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-400 font-bold text-lg">Z</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-2">Ongoing Optimization</h4>
                    <p className="text-sm text-slate-400">
                      Our support specialists continuously optimize your contact center for maximum efficiency and customer satisfaction.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="section-padding relative">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="glass-card p-8 rounded-3xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4">
                  <BarChart3 className="w-12 h-12 text-accent-500/20" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-white mb-6">Estimated Impact</h3>
                <div className="space-y-6">
                  {[
                    { label: 'Agent Productivity', value: '+35%', color: 'text-success-500' },
                    { label: 'Customer Satisfaction', value: '+20%', color: 'text-accent-400' },
                    { label: 'Cost Per Interaction', value: '-25%', color: 'text-electric-400' },
                  ].map((item) => (
                    <div key={item.label} className="flex justify-between items-center border-b border-white/5 pb-4">
                      <span className="text-slate-400">{item.label}</span>
                      <span className={`text-2xl font-bold ${item.color}`}>{item.value}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-xs text-slate-500 italic">
                  *Results based on average UC Advisors client transformations in the financial sector.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <Badge className="mb-4">Value Proposition</Badge>
              <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-6">
                Maximize Your <span className="text-gradient">Contact Center ROI</span>
              </h2>
              <p className="text-lg text-slate-400 mb-6">
                We don't just find you a platform; we optimize your entire customer engagement strategy. By leverage AI and omnichannel tools, we help you reduce costs while significantly improving member experience.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-accent-500" />
                  Self-service automation to reduce agent load
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-accent-500" />
                  Unified view of the member journey
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-accent-500" />
                  AI-driven coaching for agent excellence
                </li>
              </ul>
              <Link to="/contact">
                <Button variant="outline" className="group">
                  Get Your ROI Analysis
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  )
}

